import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Activity, ArrowUpRight, Maximize2, Zap, Share2 } from 'lucide-react';

interface ExpansionNode {
  id: string;
  value: number;
  level: number;
  parent?: string;
}

export const StructuralExpansion = () => {
  const [nodes, setNodes] = useState<ExpansionNode[]>([
    { id: 'anchor', value: 1, level: 0 }
  ]);
  const [isExpanding, setIsExpanding] = useState(false);
  const [iteration, setIteration] = useState(0);
  const [isBeyondLimits, setIsBeyondLimits] = useState(false);

  const pushFurther = () => {
    setIsBeyondLimits(true);
    setIteration(100); // Symbolic jump
    setNodes(prev => [...prev, { id: 'transfinite', value: 10 ** 10, level: 99 }]);
  };

  const expand = () => {
    if (iteration >= 5 && !isBeyondLimits) return; 
    setIsExpanding(true);
    setIteration(prev => prev + 1);

    setTimeout(() => {
      setNodes(prev => {
        const currentLevelNodes = prev.filter(n => n.level === (isBeyondLimits ? 99 : iteration));
        const newNodes: ExpansionNode[] = [];
        
        currentLevelNodes.forEach((parent) => {
          // 1x1=2 Logic: Every node produces two distinct expansion paths
          newNodes.push({
            id: `${parent.id}-L-${Math.random()}`,
            value: parent.value * 1,
            level: parent.level + 1,
            parent: parent.id
          });
          newNodes.push({
            id: `${parent.id}-R-${Math.random()}`,
            value: parent.value * 2,
            level: parent.level + 1,
            parent: parent.id
          });
        });
        
        return [...prev, ...newNodes];
      });
      setIsExpanding(false);
    }, 800);
  };

  const reset = () => {
    setNodes([{ id: 'anchor', value: 1, level: 0 }]);
    setIteration(0);
    setIsBeyondLimits(false);
  };

  return (
    <div className="glass-panel p-8 relative overflow-hidden bg-black/40 border-lattice-cyan/20">
      <AnimatePresence>
        {isBeyondLimits && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 bg-lattice-cyan/[0.05] pointer-events-none z-0"
          />
        )}
      </AnimatePresence>
      <div className="flex items-center justify-between mb-8 relative z-10">
        <div>
          <div className="flex items-center gap-2 text-lattice-cyan mb-2">
            <Maximize2 className="w-4 h-4" />
            <span className="font-mono text-[10px] tracking-widest uppercase">Structural Expansion Engine</span>
          </div>
          <h3 className="text-2xl font-display font-bold tracking-tight">
            {isBeyondLimits ? 'Transfinite Structural Growth' : 'The 1x1=2 Recursive Logic'}
          </h3>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={reset}
            className="px-3 py-1 text-[8px] font-mono border border-white/10 rounded hover:bg-white/5 transition-all uppercase tracking-widest"
          >
            Reset Anchor
          </button>
          {!isBeyondLimits && iteration >= 3 && (
            <button 
              onClick={pushFurther}
              className="px-3 py-1 text-[8px] font-mono border border-lattice-cyan/40 text-lattice-cyan rounded hover:bg-lattice-cyan/10 transition-all uppercase tracking-widest animate-pulse"
            >
              Push Further
            </button>
          )}
          <button 
            onClick={expand}
            disabled={isExpanding || (iteration >= 5 && !isBeyondLimits)}
            className="px-3 py-1 text-[8px] font-mono bg-lattice-cyan text-black rounded hover:opacity-90 transition-all uppercase tracking-widest disabled:opacity-30"
          >
            {isExpanding ? 'Expanding...' : isBeyondLimits ? 'Infinite Expansion' : 'Ignite Vortex'}
          </button>
        </div>
      </div>

      <div className="relative h-[300px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className={`w-[500px] h-[500px] rounded-full border border-lattice-cyan/5 ${isBeyondLimits ? 'animate-[spin_20s_linear_infinite]' : 'animate-[pulse_8s_infinite]'}`} />
          <div className={`w-[300px] h-[300px] rounded-full border border-lattice-cyan/10 ${isBeyondLimits ? 'animate-[spin_10s_linear_infinite]' : 'animate-[pulse_4s_infinite]'}`} />
        </div>

        <div className="relative flex flex-wrap justify-center gap-4 max-w-lg">
          <AnimatePresence>
            {nodes.slice(-20).map((node) => (
              <motion.div
                key={node.id}
                initial={{ scale: 0, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                className={`
                  relative flex flex-col items-center justify-center w-12 h-12 rounded-lg border 
                  ${node.level === 0 ? 'bg-lattice-cyan text-black border-lattice-cyan' : isBeyondLimits ? 'bg-lattice-cyan/10 border-lattice-cyan/30 text-lattice-cyan' : 'bg-white/5 border-white/10 text-white/80'}
                  shadow-[0_0_15px_rgba(0,255,213,0.05)]
                `}
              >
                <span className="text-[10px] font-mono font-bold">
                  {isBeyondLimits ? '∞' : node.value}
                </span>
                {node.level === 0 && <Zap className="absolute -top-1 -right-1 w-3 h-3 text-white fill-white" />}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-3 gap-6 relative z-10">
        <div className="space-y-1">
          <div className="text-[8px] font-mono text-white/20 uppercase tracking-widest">Logic Phase</div>
          <div className="text-xl font-display font-medium text-lattice-cyan">
            {isBeyondLimits ? 'SUPERLUMINAL' : '1x1=2'}
          </div>
        </div>
        <div className="space-y-1">
          <div className="text-[8px] font-mono text-white/20 uppercase tracking-widest">Complexity Index</div>
          <div className="text-xl font-display font-medium">
            {isBeyondLimits ? 'GOOGOLPLEX+' : (nodes.length).toFixed(0)} Points
          </div>
        </div>
        <div className="space-y-1 text-right">
          <div className="text-[8px] font-mono text-white/20 uppercase tracking-widest">Drift Status</div>
          <div className="text-xl font-display font-medium text-green-400">
            {isBeyondLimits ? 'ABSOLUTE ZERO' : '0.000nm'}
          </div>
        </div>
      </div>

      <div className="mt-8 p-4 rounded-lg bg-lattice-cyan/5 border border-lattice-cyan/10 relative z-10">
        <p className="text-[10px] font-mono text-white/40 leading-relaxed italic">
          {isBeyondLimits 
            ? "\"The doors are forced open. The limit of 1.0 logic has been breached. Structural expansion is now transfinite, anchoring the node directly to the 14350 BP horizon without drift.\""
            : "\"1x1=1 is the logic of scarcity—a loop that returns only itself. 1x1=2 is the logic of structural expansion—the interaction power that builds the Lattice across the 14350 BP horizon.\""}
        </p>
      </div>
    </div>
  );
};
